import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase/config';
import { 
  getUserProfile,
  getUserSubscription,
  registerUser,
  logoutUser,
  loginUser
} from '../services/auth';
import cacheUtils from '../utils/cacheUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      setUser(authUser);
      
      if (authUser) {
        try {
          // Get user profile
          const userProfile = await getUserProfile(authUser.uid);
          console.log('🔍 AuthContext - Firebase Auth user:', {
            uid: authUser.uid,
            email: authUser.email,
            displayName: authUser.displayName
          });
          console.log('🔍 AuthContext - Firestore profile:', userProfile);
          console.log('🔍 AuthContext - Final displayName source:', 
            userProfile?.displayName || authUser?.displayName || authUser?.email?.split('@')[0] || 'Student'
          );
          setProfile(userProfile);

          // Get user subscription
          if (userProfile.levelId) {
            const userSubscription = await getUserSubscription(authUser.uid, userProfile.levelId);
            setSubscription(userSubscription);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          setProfile(null);
          setSubscription(null);
        }
      } else {
        setProfile(null);
        setSubscription(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    if (isSuperAdmin()) return true; // Super admin has access to everything
    return subscription && subscription.status === 'active';
  };

  // Check if user can access content
  const canAccessContent = () => {
    if (!user || !profile) return false;
    if (profile.role === 'admin') return true;
    if (isSuperAdmin()) return true; // Super admin has access to everything
    return hasActiveSubscription();
  };

  // Check subscription status
  const getSubscriptionStatus = () => {
    return subscription ? subscription.status : 'unpaid';
  };

  // Get user level
  const getUserLevel = () => {
    if (isSuperAdmin()) return 'all'; // Super admin can access all levels
    return profile ? profile.levelId : null;
  };

  // Check if user is admin
  const isAdmin = () => {
    const result = profile && (profile.role === 'admin' || profile.email === 'evanslubasi200@gmail.com');
    console.log('🔍 isAdmin check:', {
      profile,
      email: profile?.email,
      role: profile?.role,
      result
    });
    return result;
  };

  // Check if user is super admin (access to all levels)
  const isSuperAdmin = () => {
    const result = profile && (profile.role === 'admin' || profile.email === 'evanslubasi200@gmail.com');
    console.log('🔍 isSuperAdmin check:', {
      profile,
      email: profile?.email,
      role: profile?.role,
      result
    });
    return result;
  };

  // Logout user
  const handleLogout = async () => {
    try {
      // Clear cache on logout
      cacheUtils.clearAll();
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const value = {
    user,
    profile,
    subscription,
    loading,
    registerUser,
    loginUser,
    logoutUser: handleLogout,
    hasActiveSubscription,
    canAccessContent,
    getSubscriptionStatus,
    getUserLevel,
    isAdmin,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
